import { measureLatency } from '../../../gctl-measure';
import { CommentFields, Comment } from '../../../models/components/schemas/comment/Comment';
import {
  Select,
  NetworkError,
  deepCamelCase,
  deepSnakeCase,
  searchQueryString,
  intoSnakeCase,
} from '../../../fetchers/_helpers';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type GetVideoCommentRepliesFields = readonly CommentFields[];

/** Options for GET /videos/{video_id}/comments/{comment_id}/replies */
export type GetVideoCommentRepliesOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.comment+json */
  select: Paths;
  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
    /**
     * The ID of the comment.
     */
    commentId: number;
  };

  /** Possible query parameters for /videos/{video_id}/comments/{comment_id}/replies */
  query?: {
    /**
     * The page number of the results to show.
     */
    page?: number;

    /**
     * The number of items to show on each page of results, up to a maximum of 100.
     */
    perPage?: number;
  };
};

/** Result type for GET /videos/{video_id}/comments/{comment_id}/replies */
export type GetVideoCommentRepliesResult<Paths extends readonly string[]> = {
  /** The total number of results for GET /videos/{video_id}/comments/{comment_id}/replies */
  total: number;
  /** The current page */
  page: number;
  /** The number of results per page specified in the request */
  perPage: number;
  /** Available API endpoints for traversing the paginated data */
  paging: {
    /** The next page, if available */
    next: string | null;
    /** The previous page, if available */
    previous: string | null;
    /** The first page */
    first: string;
    /** The last page */
    last: string;
  };
  /** An array containing the current results */
  data: Select<Comment, Paths>[];
};

/**
 * Get all the replies to a video comment
 * This method returns every reply to the specified video comment.
 */
export async function getVideoCommentReplies<Paths extends GetVideoCommentRepliesFields>({
  baseUrl,
  select,
  where: { videoId, commentId },
  query,
  ...options
}: GetVideoCommentRepliesOptions<Paths>): Promise<GetVideoCommentRepliesResult<Paths>> {
  return measureLatency('getVideoCommentReplies', 'GET', async () => {
    let res = await fetch(
      `${baseUrl}/videos/${videoId}/comments/${commentId}/replies?${searchQueryString(
        query,
      )}&fields=${select.map(intoSnakeCase).join(',')}`,
      {
        ...options,
        method: 'GET',
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as GetVideoCommentRepliesResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}

export type PostVideoCommentRepliesFields = readonly CommentFields[];

/** Options for POST /videos/{video_id}/comments/{comment_id}/replies */
export type PostVideoCommentRepliesOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.comment+json */
  select: Paths;
  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
    /**
     * The ID of the comment.
     */
    commentId: number;
  };

  /** Possible query parameters for /videos/{video_id}/comments/{comment_id}/replies */
  query?: {
    /**
     * The hashed password of the password-protected video.
     */
    password?: string;
  };

  /** Data serialized to body for POST /videos/{video_id}/comments/{comment_id}/replies */
  variables: {
    /**
     * The rich comment in JSON stringified form.
     * @example {"type":"doc","content":[{"type":"paragraph","content":[{"type":"text","text":"I love this!"}]}]}
     */

    richtext?: string;

    /**
     * The reply to the comment.
     * @example I love this!
     */

    text: string;
  };
};

/** Result type for POST /videos/{video_id}/comments/{comment_id}/replies */
export type PostVideoCommentRepliesResult<Paths extends readonly string[]> = Select<Comment, Paths>;

/**
 * Add a reply to a video comment
 * This method adds a reply to the specified video comment.
 */
export async function postVideoCommentReplies<Paths extends PostVideoCommentRepliesFields>({
  baseUrl,
  select,
  variables,
  where: { videoId, commentId },
  query,
  ...options
}: PostVideoCommentRepliesOptions<Paths>): Promise<PostVideoCommentRepliesResult<Paths>> {
  return measureLatency('postVideoCommentReplies', 'POST', async () => {
    let res = await fetch(
      `${baseUrl}/videos/${videoId}/comments/${commentId}/replies?${searchQueryString(
        query,
      )}&fields=${select.map(intoSnakeCase).join(',')}`,
      {
        ...options,
        method: 'POST',
        body: JSON.stringify(deepSnakeCase(variables)),
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as PostVideoCommentRepliesResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}
